import { Head, useForm } from "@inertiajs/react"
import { Button, Flex, Heading, Text } from "@radix-ui/themes"
import { z } from "zod"

import ImportPreviewTable from "@/components/import-preview-table"
import InputError from "@/components/input-error"
import ProjectLayout from "@/layouts/project-layout"
import { projectImportPath, projectImportsPath } from "@/routes"
import { ImportSchema, ProjectSchema } from "@/schemas"

const PropsSchema = z.object({
  project: ProjectSchema,
  import: ImportSchema,
  headers: z.array(z.string()),
  preview_data: z.array(z.array(z.string().nullable())),
  column_options: z.array(z.tuple([z.string(), z.number()])),
})

type Props = z.infer<typeof PropsSchema>

export default function ShowPendingImport(props: Props) {
  const validatedProps = PropsSchema.parse(props)
  const { project, import: importData, headers, preview_data, column_options } = validatedProps

  const { data, setData, patch, processing, errors } = useForm({
    email_column_index: importData.email_column_index ?? "",
    custom_field_indices: importData.custom_field_indices || [],
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(projectImportPath(project.slug, importData.id))
  }

  const toggleCustomField = (columnIndex: number) => {
    const currentIndices = data.custom_field_indices
    if (currentIndices.includes(columnIndex)) {
      setData(
        "custom_field_indices",
        currentIndices.filter((idx) => idx !== columnIndex),
      )
    } else {
      setData("custom_field_indices", [...currentIndices, columnIndex])
    }
  }

  return (
    <ProjectLayout
      projectId={project.slug}
      breadcrumbs={[
        { title: project.name, href: projectImportsPath(project.slug) },
        { title: "Imports", href: projectImportsPath(project.slug) },
        { title: "Import CSV file" },
      ]}
    >
      <Head title="Import CSV file" />

      <Flex direction="column" gap="6" p="6">
        <div>
          <Heading size="6" weight="medium" mb="6">
            Import CSV file
          </Heading>

          <form onSubmit={handleSubmit}>
            <Flex direction="column" gap="4" mb="6">
              <Flex align="center" gap="4">
                <Text as="div" size="3" weight="bold" className="w-32">
                  Email column
                </Text>

                <div className="flex-1">
                  <select
                    value={data.email_column_index.toString()}
                    onChange={(e) => setData("email_column_index", parseInt(e.target.value, 10))}
                    className="w-96 rounded border border-gray-300 text-sm"
                  >
                    <option value="">Select email column</option>
                    {column_options.map(([label, value]) => (
                      <option key={value} value={value.toString()}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <InputError message={errors.email_column_index} />
                </div>
              </Flex>

              <Flex gap="4">
                <Text as="div" size="3" weight="bold" className="w-32">
                  Custom fields to import
                </Text>

                <div className="flex-1">
                  <div className="max-h-48 w-96 overflow-y-auto rounded border border-stone-300 p-2">
                    <div className="flex flex-col gap-2">
                      {column_options.map(([label, value]) => (
                        <label key={value} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={data.custom_field_indices.includes(value)}
                            onChange={() => toggleCustomField(value)}
                            className="rounded border-gray-300 checked:border-blue-600 focus:ring-0 focus:ring-offset-0"
                          />
                          <span className="text-sm">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </Flex>

              <div className="mt-4 flex items-center gap-4">
                <Text as="div" size="3" weight="bold" className="w-32"></Text>

                <Button type="submit" disabled={processing || data.email_column_index === ""}>
                  {processing ? "Processing import..." : "Process import"}
                </Button>
              </div>
            </Flex>
          </form>
        </div>

        <Heading size="5" weight="medium" mb="4">
          Preview (total {importData.file_count_lines})
        </Heading>

        <ImportPreviewTable
          headers={headers}
          preview_data={preview_data}
          file_count_lines={importData.file_count_lines}
        />
      </Flex>
    </ProjectLayout>
  )
}
